import { useState } from "react"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import { evictLicenseSeatQueries } from "@/v2-backoffice-client/license/license.utils"
import { LICENSE_SOURCE_OPERATOR } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseSeatCountFieldProps = {
  license: LicenseFragment
}

// The seat count of a limited license, edited from inside the Seats dialog
// next to the names it is held against. The count is what the customer pays
// for and is reported, never a lock (FUS-2766): changing or removing it
// moves nobody in or out. Which column it writes depends on who owns the
// row, as "Edit Seat Cap" does: an operator license carries Seats (set /
// clear, FUS-2724); a billing-derived license carries the override (set /
// clear back to billing's quantity), since the sync rewrites Seats.
function LicenseSeatCountField(props: LicenseSeatCountFieldProps) {
  const { license } = props
  const isOperator = license.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
  const [seats, setSeats] = useState(
    license.Seats != null ? String(license.Seats) : "",
  )

  const [updateLicense, { loading }] = useLicenseUpdateMutation({
    update: evictLicenseSeatQueries,
    onCompleted(data) {
      const updated = data.updateLicense
      setSeats(updated.Seats != null ? String(updated.Seats) : "")
      toast({
        variant: "success",
        title: "Seat count updated",
        description:
          updated.Seats != null
            ? `${updated.CustomerName ?? "The customer"} is now licensed for ${updated.Seats} ${updated.Seats === 1 ? "seat" : "seats"}.`
            : `${updated.CustomerName ?? "The customer"}'s license carries no seat count.`,
      })
    },
    onError(error) {
      toast({
        variant: "destructive",
        title: "Seat count not saved",
        description: error.message,
      })
    },
  })

  const parsed = Number(seats)
  const isValid = seats !== "" && Number.isInteger(parsed) && parsed >= 1
  const isUnchanged = isValid && parsed === license.Seats
  // A billing row's count can only be taken back to billing's quantity; a
  // row whose count IS billing's quantity has nothing to clear.
  const canClear = isOperator
    ? license.Seats != null
    : license.SeatsOverride != null

  return (
    <div className="mb-4 flex items-end gap-3 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 flex-1 space-y-1">
        <Label htmlFor="licenseSeatCount">Seats paid for</Label>
        <Input
          id="licenseSeatCount"
          type="number"
          min={1}
          placeholder="None"
          value={seats}
          onChange={(e) => {
            setSeats(e.target.value)
          }}
        />
        <p className="text-xs text-muted-foreground">
          {isOperator
            ? `What ${license.CustomerName ?? "the customer"} pays for. ${license.SeatsAssigned} named today; more names than seats shows as over on the license row, nobody is locked out by it.`
            : license.BillingSeats != null
              ? `From the Fenerum subscription: ${license.BillingSeats}. A number saved here overrides it for this customer and survives every sync.`
              : "The Fenerum subscription is not sold per seat. A number saved here is this customer's own count and survives every sync."}
        </p>
      </div>
      {canClear && (
        <Button
          type="button"
          variant="outline"
          isLoading={loading}
          data-feature-code="UI_BackofficeLicense_Seats_ClearCount"
          onClick={() => {
            updateLicense({
              variables: {
                id: license.Id,
                input: isOperator
                  ? { ClearSeats: true }
                  : { ClearSeatsOverride: true },
              },
            })
          }}
        >
          {isOperator ? "Remove" : "Use Billing"}
        </Button>
      )}
      <Button
        type="button"
        isLoading={loading}
        disabled={!isValid || isUnchanged}
        data-feature-code="UI_BackofficeLicense_Seats_SaveCount"
        onClick={() => {
          updateLicense({
            variables: {
              id: license.Id,
              input: isOperator ? { Seats: parsed } : { SeatsOverride: parsed },
            },
          })
        }}
      >
        Save
      </Button>
    </div>
  )
}

export default LicenseSeatCountField
